import { Injectable } from '@angular/core';

export interface LogEntry {
  timestamp: number;
  level: 'info' | 'warn' | 'error' | 'debug';
  service: string;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class LogCollectorService {
  private logs: LogEntry[] = [];
  private maxLogs = 500; // Keep last 500 logs
  private enabled = true;

  constructor() {
    console.log('🔧 LogCollectorService: Initialized');

    // Make service available globally for easy access from browser console
    (window as any).logCollector = this;
    console.log('💡 Tip: Use window.logCollector.printLogs() in console to see all logs');
    console.log('💡 Tip: Use window.logCollector.copyLogs() to copy logs to clipboard');
  }

  // Convenience method to print logs to console
  printLogs(filterService?: string) {
    const logs = filterService ? this.getLogsByService(filterService) : this.logs;
    console.log('='.repeat(80));
    console.log(`📋 Collected Logs (${logs.length} entries)`);
    console.log('='.repeat(80));
    logs.forEach(log => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      const icon = log.level === 'error' ? '❌' : log.level === 'warn' ? '⚠️' : log.level === 'debug' ? '🐛' : '✅';
      console.log(`${icon} [${time}] [${log.service}] ${log.message}`);
      if (log.data) {
        console.log('   Data:', log.data);
      }
    });
    console.log('='.repeat(80));
  }

  // Copy logs to clipboard (if available)
  async copyLogs(filterService?: string) {
    const text = filterService
      ? this.getLogsByService(filterService).map(l => this.formatLogEntry(l)).join('\n')
      : this.getLogsAsText();

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        console.log('✅ Logs copied to clipboard!');
        alert('Logs copied to clipboard!');
      } else {
        // Fallback: show in alert for manual copy
        console.log('📋 Copy these logs:\n\n' + text);
        alert('Clipboard API not available. Check console for logs.');
      }
    } catch (error) {
      console.error('Failed to copy logs:', error);
      console.log('📋 Logs:\n\n' + text);
    }
  }

  private formatLogEntry(log: LogEntry): string {
    const time = new Date(log.timestamp).toISOString();
    const dataStr = log.data ? ` | Data: ${JSON.stringify(log.data)}` : '';
    return `[${time}] [${log.level.toUpperCase()}] [${log.service}] ${log.message}${dataStr}`;
  }

  log(service: string, message: string, data?: any, level: 'info' | 'warn' | 'error' | 'debug' = 'info') {
    if (!this.enabled) return;

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      service,
      message,
      data: data ? this.sanitizeData(data) : undefined
    };

    this.logs.push(entry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also log to console
    const prefix = `🔧 [${service}]`;
    switch (level) {
      case 'error':
        console.error(prefix, message, data || '');
        break;
      case 'warn':
        console.warn(prefix, message, data || '');
        break;
      case 'debug':
        console.debug(prefix, message, data || '');
        break;
      default:
        console.log(prefix, message, data || '');
    }
  }

  private sanitizeData(data: any): any {
    try {
      // Clone and truncate large strings to prevent memory issues
      if (typeof data === 'string' && data.length > 1000) {
        return data.substring(0, 1000) + '... (truncated)';
      }
      if (typeof data === 'object') {
        return JSON.parse(JSON.stringify(data, (key, value) => {
          if (typeof value === 'string' && value.length > 1000) {
            return value.substring(0, 1000) + '... (truncated)';
          }
          return value;
        }));
      }
      return data;
    } catch (e) {
      return String(data);
    }
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  getLogsAsText(): string {
    return this.logs.map(log => {
      const time = new Date(log.timestamp).toISOString();
      const dataStr = log.data ? ` | Data: ${JSON.stringify(log.data, null, 2)}` : '';
      return `[${time}] [${log.level.toUpperCase()}] [${log.service}] ${log.message}${dataStr}`;
    }).join('\n');
  }

  // Get logs filtered by service name
  getLogsByService(service: string): LogEntry[] {
    return this.logs.filter(log => log.service === service);
  }

  // Get logs filtered by time range (in milliseconds)
  getLogsByTimeRange(startTime: number, endTime: number): LogEntry[] {
    return this.logs.filter(log => log.timestamp >= startTime && log.timestamp <= endTime);
  }

  // Get recent logs (last N seconds)
  getRecentLogs(seconds: number = 30): LogEntry[] {
    const cutoffTime = Date.now() - (seconds * 1000);
    return this.logs.filter(log => log.timestamp >= cutoffTime);
  }

  // Export logs as downloadable text file
  exportLogsAsFile(): string {
    return this.getLogsAsText();
  }

  // Export logs as JSON
  exportLogsAsJSON(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  clear() {
    this.logs = [];
    console.log('🔧 LogCollectorService: Logs cleared');
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    console.log('🔧 LogCollectorService: Logging', enabled ? 'enabled' : 'disabled');
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  // Get summary of recent activity
  getSummary(seconds: number = 30): string {
    const recentLogs = this.getRecentLogs(seconds);
    const summary = {
      total: recentLogs.length,
      info: recentLogs.filter(l => l.level === 'info').length,
      warn: recentLogs.filter(l => l.level === 'warn').length,
      error: recentLogs.filter(l => l.level === 'error').length,
      debug: recentLogs.filter(l => l.level === 'debug').length,
      services: [...new Set(recentLogs.map(l => l.service))]
    };

    return `
📊 Log Summary (Last ${seconds} seconds):
- Total Logs: ${summary.total}
- Info: ${summary.info}
- Warnings: ${summary.warn}
- Errors: ${summary.error}
- Debug: ${summary.debug}
- Active Services: ${summary.services.join(', ')}
    `.trim();
  }
}
